import {
  useEffect, useState, useRef, useCallback,
} from 'react';
import { Message } from '@/widget/domain/message';
import MessagesDispatcher from '@/widget/application/messages/MessagesDispatcher';
import moment from 'moment';
import { useChatWidgetContext } from '@/widget/services/useChatWidgetContext';
import { useStore } from 'zustand';
import generateUUID from '@/lib/generateUUID';

export default function useConversation() {
  const { store, settings } = useChatWidgetContext();
  const {
    customTrigger,
    formFields,
  } = settings;

  const templateToStart = useStore(store, (state) => state.templateToStart);
  const messages = useStore(store, (state) => state.messages);
  const conversations = useStore(store, (state) => state.conversations);
  const delayedMessage = useStore(store, (state) => state.delayedMessage);
  const subscription = useStore(store, (state) => state.widgetSubscription);
  const activeConversationKey = useStore(store, (state) => state.activeConversationKey);
  const contactInfo = useStore(store, (state) => ({
    companyId: state.companyId,
    contactId: state.contactId,
    userId: state.userId,
    accountId: state.accountId,
    orgName: state.orgName,
  }));
  const { setTemplateToStart, setDelayedMessage } = store.getState();
  const key = useRef(activeConversationKey);
  const [displayForm, setDisplayForm] = useState(false);
  const [shouldBeLoaded, setShouldBeLoaded] = useState(
    !!conversations && conversations.map((c) => c.key).includes(key.current),
  );

  const addMessage = useCallback((conversationKey: string, message: Message) => {
    if (!customTrigger && !contactInfo.contactId && formFields.length) {
      setDelayedMessage(message);
      setDisplayForm(true);
    } else {
      MessagesDispatcher.sendMessage({
        conversationKey,
        message,
        contactInfo,
        store,
        subscription,
      });
    }
  }, [contactInfo, customTrigger, formFields.length, setDelayedMessage, store, subscription]);

  useEffect(() => {
    if (shouldBeLoaded) {
      setShouldBeLoaded(
        MessagesDispatcher.requestData({
          subscription,
          conversationKey: key.current,
        }),
      );
    }
  }, [key, shouldBeLoaded, subscription]);

  useEffect(() => {
    if (displayForm && contactInfo.contactId && delayedMessage) {
      addMessage(activeConversationKey, delayedMessage);
      setDisplayForm(false);
      setDelayedMessage(null);
    }
  }, [activeConversationKey, addMessage, contactInfo.contactId, delayedMessage, displayForm, setDelayedMessage]);

  useEffect(() => {
    if (!shouldBeLoaded && !!templateToStart) {
      addMessage(key.current, {
        id: generateUUID(),
        fromOperator: false,
        date: moment(new Date()).format(),
        text: templateToStart,
        sent: false,
      });
      setTemplateToStart(null);
    }
  }, [addMessage, setTemplateToStart, shouldBeLoaded, templateToStart]);

  return {
    messages,
    addMessage,
    displayForm,
    delayedMessage,
  };
}
